import {
  ConnectionType,
  Flex,
  MixpanelEvent,
  VStack,
  useMarketAccountsContext,
  useMixpanel,
  type Security,
} from '@talos/kyoko';
import { forwardRef, useCallback, useMemo, type ForwardedRef } from 'react';
import { MarketAccountSearchList } from '../../../../components/MarketAccountSelectionList/MarketAccountSearchList';

interface MarketsSelectorProps {
  security: Security;
  selectedMarkets: string[];
  setSelectedMarkets: (selectedMarkets: string[]) => void;
  selectedMarketAccounts: string[];
  setSelectedMarketAccounts: (selectedMarketAccounts: string[]) => void;
}

export const MarketsSelector = forwardRef(
  (
    {
      security,
      selectedMarkets,
      setSelectedMarkets,
      selectedMarketAccounts,
      setSelectedMarketAccounts,
    }: MarketsSelectorProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const mixpanel = useMixpanel();
    const { marketAccountsList } = useMarketAccountsContext();

    const securityMarkets = useMemo(() => security?.Markets || [], [security]);
    const securityMarketAccounts = useMemo(
      () => marketAccountsList?.filter(ma => securityMarkets.includes(ma.Market)).map(ma => ma.Name) || [],
      [marketAccountsList, securityMarkets]
    );

    const handleMarketAccountsChange = useCallback(
      (newSelectedMarketAccounts: string[]) => {
        mixpanel.track(MixpanelEvent.UpdateMarketSelection);
        setSelectedMarketAccounts(newSelectedMarketAccounts.toSorted());
      },
      [mixpanel, setSelectedMarketAccounts]
    );

    const handleMarketsChange = useCallback(
      (newSelectedMarkets: string[]) => {
        mixpanel.track(MixpanelEvent.UpdateMarketSelection);
        setSelectedMarkets(newSelectedMarkets.toSorted());
      },
      [mixpanel, setSelectedMarkets]
    );

    return (
      <Flex h="100%" flex="1" minHeight="250px">
        <VStack w="100%" flex="1" alignItems="flex-start" justifyContent="flex-start">
          <MarketAccountSearchList
            ref={ref}
            availableMarkets={securityMarkets}
            availableMarketAccounts={securityMarketAccounts}
            selectedMarketAccounts={selectedMarketAccounts}
            selectedMarkets={selectedMarkets}
            security={security}
            scrollContent={true}
            onChangeMarketAccounts={handleMarketAccountsChange}
            onChangeMarkets={handleMarketsChange}
            connectionType={ConnectionType.MarketData}
            showInactiveMarketAccounts={false}
          />
        </VStack>
      </Flex>
    );
  }
);
